/**
 * Single Evidence Contract (SEC) — unified field resolution for the
 * Coverage Matrix, Variables browser, report manifests, and CAF.
 *
 * Field values delegate to `resolveReportFields`; requirement status
 * delegates to `computeRequirementStatus` with conflict caps applied.
 */
import type { ChecklistItem } from '../../config/documentChecklists';
import type { ReportTypeId } from '../../config/reportTypes';
import type { ExtractedClaim } from '../../types/siteContext';
import type { SiteDocument } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { RequirementCoverage } from './requirementCoverage';
import type { FieldPath } from '../evidenceLineage/requirementToFields';
import { getFieldAlignment } from './requirementFieldAlignment';
import {
  resolveReportFields,
  type ReportPrimaryField,
  type ReportRelatedField,
} from './resolveReportFields';
import {
  computeRequirementStatus,
  type RequirementStatus,
} from './requirementStatus';
import {
  capRequirementStatusForConflicts,
  unresolvedConflictsForRequirement,
} from './requirementConflictCaps';
import { mapSupplyChainPlanToCoverage, VENDOR_QUOTE_PACK_FIELD_KEYS } from './supplyChainCoverageFill';
import { formatFieldValue } from '../evidenceLineage/requirementToFields';

export type EvidenceSourceTier =
  | 'user'
  | 'doc'
  | 'sed'
  | 'dam'
  | 'research'
  | 'provider'
  | 'report-derived'
  | 'car'
  | 'missing';

export interface ResolvedEvidenceField {
  key: string;
  label: string;
  value: unknown;
  displayValue: string;
  sourceTier: EvidenceSourceTier;
  sourceRef: string;
  sedPath?: FieldPath;
  confidence: number;
  isEmpty: boolean;
}

export interface ConsolidatedRequirementEvidence {
  requirementId: string;
  fields: ResolvedEvidenceField[];
  related: ReportRelatedField[];
  status: RequirementStatus;
  /** Status after unresolved conflict cap (may downgrade strong → adequate). */
  effectiveStatus: RequirementStatus;
  hasUnresolvedConflicts: boolean;
  structuredFilled: number;
  structuredTotal: number;
  statusFilled: number;
  statusTotal: number;
}

export interface CoverageManifestEntry {
  requirementId: string;
  fields: Array<{
    key: string;
    label: string;
    value: unknown;
    displayValue: string;
    sourceTier: EvidenceSourceTier;
    sourceRef: string;
  }>;
}

export interface CoverageManifestAtGen {
  reportType: ReportTypeId;
  generatedAt: string;
  entries: CoverageManifestEntry[];
}

function provenanceToTier(
  provenance: ReportPrimaryField['provenance'],
): EvidenceSourceTier {
  switch (provenance) {
    case 'authored': return 'user';
    case 'doc':
    case 'doc-override': return 'doc';
    case 'dam': return 'dam';
    case 'research': return 'research';
    case 'provider':
    case 'provider+doc': return 'provider';
    case 'report-derived': return 'report-derived';
    case 'verified-from-kmz': return 'sed';
    default: return 'missing';
  }
}

function sourceRefForField(
  requirementId: string,
  field: ReportPrimaryField,
  claim?: ExtractedClaim,
): string {
  if (field.provenance === 'authored' && claim) {
    return `authored:${requirementId}:${claim.lastWrittenBy?.uid ?? claim.id}`;
  }
  if (field.provenance === 'missing') return '';
  const alignment = getFieldAlignment(requirementId, field.key);
  const path = alignment?.fieldPath ?? `${requirementId}.${field.key}`;
  return `sed:${path}:${field.provenance}`;
}

function fieldsFromResolved(
  requirementId: string,
  primary: ReportPrimaryField[],
  claim?: ExtractedClaim,
): ResolvedEvidenceField[] {
  return primary.map(f => ({
    key: f.key,
    label: f.label,
    value: f.value,
    displayValue: f.displayValue,
    sourceTier: provenanceToTier(f.provenance),
    sourceRef: sourceRefForField(requirementId, f, claim),
    sedPath: getFieldAlignment(requirementId, f.key)?.fieldPath,
    confidence: f.provenance === 'authored' ? (claim?.extractorConfidence ?? 0.75) : 0.8,
    isEmpty: f.isEmpty,
  }));
}

export interface ResolveEvidenceForRequirementArgs {
  requirement: ChecklistItem;
  sed?: SiteEvidenceDocument;
  documents?: readonly SiteDocument[];
  authoredClaims?: readonly ExtractedClaim[];
  coverageEnvelope?: RequirementCoverage;
}

/** SEC entry point — one requirement row. */
export function resolveEvidenceForRequirement(
  args: ResolveEvidenceForRequirementArgs,
): ConsolidatedRequirementEvidence {
  const { requirement, sed, documents = [], authoredClaims = [], coverageEnvelope } = args;
  const claim = authoredClaims.find(c => c.requirementId === requirement.id);
  const resolved = resolveReportFields({
    requirement,
    sed,
    authoredClaim: claim,
  });
  const fields = fieldsFromResolved(requirement.id, resolved.primary, claim);
  const status = computeRequirementStatus({
    requirement,
    sed,
    documents,
    authoredClaims,
  });
  const conflicts = unresolvedConflictsForRequirement(sed, requirement.id);
  const hasUnresolvedConflicts = conflicts.length > 0;
  const effectiveStatus = capRequirementStatusForConflicts(status, hasUnresolvedConflicts);

  if (coverageEnvelope && coverageEnvelope.fillSource !== 'missing') {
    for (const f of fields) {
      if (f.isEmpty && coverageEnvelope.sourceRef) {
        f.sourceRef = coverageEnvelope.sourceRef;
        f.sourceTier = mapFillSourceToTier(coverageEnvelope.fillSource);
      }
    }
  }

  return {
    requirementId: requirement.id,
    fields,
    related: resolved.related,
    status,
    effectiveStatus,
    hasUnresolvedConflicts,
    structuredFilled: resolved.structuredFilled,
    structuredTotal: resolved.structuredTotal,
    statusFilled: resolved.statusFilled,
    statusTotal: resolved.statusTotal,
  };
}

function mapFillSourceToTier(
  fillSource: RequirementCoverage['fillSource'],
): EvidenceSourceTier {
  switch (fillSource) {
    case 'authored': return 'user';
    case 'doc': return 'doc';
    case 'dam-baseline': return 'dam';
    case 'provider': return 'provider';
    case 'ai-inference': return 'car';
    default: return 'missing';
  }
}

export function buildCoverageManifestAtGen(args: {
  reportType: ReportTypeId;
  checklist: readonly ChecklistItem[];
  sed?: SiteEvidenceDocument | null;
  authoredClaims?: readonly ExtractedClaim[];
  supplyChainPlan?: import('../../types/supplyChain').SupplyChainPlan;
  generatedAt?: string;
}): CoverageManifestAtGen {
  const { reportType, checklist, sed, authoredClaims = [], supplyChainPlan, generatedAt = new Date().toISOString() } = args;
  const entries: CoverageManifestEntry[] = [];
  for (const item of checklist) {
    const structured = (item.authoredSchema ?? []).filter(f => f.key !== 'notes');
    if (structured.length === 0) continue;
    const consolidated = resolveEvidenceForRequirement({
      requirement: item,
      sed: sed ?? undefined,
      authoredClaims,
    });
    let filled = consolidated.fields.filter(f => !f.isEmpty);
    if (item.id === 'vendor-quote-pack' && supplyChainPlan && supplyChainPlan.bom.length > 0) {
      const mapped = mapSupplyChainPlanToCoverage(supplyChainPlan);
      const filledKeys = new Set(filled.map(f => f.key));
      for (const key of VENDOR_QUOTE_PACK_FIELD_KEYS) {
        if (filledKeys.has(key)) continue;
        const value = mapped.fields[key];
        if (value == null || value === '') continue;
        const schemaField = structured.find(f => f.key === key);
        filled = [
          ...filled,
          {
            key,
            label: schemaField?.label ?? key,
            value,
            displayValue: formatFieldValue(value),
            sourceTier: 'provider' as const,
            sourceRef: 'supply-chain-plan',
            confidence: mapped.confidence.value,
            isEmpty: false,
          },
        ];
      }
    }
    if (filled.length === 0) continue;
    entries.push({
      requirementId: item.id,
      fields: filled.map(f => ({
        key: f.key,
        label: f.label,
        value: f.value,
        displayValue: f.displayValue,
        sourceTier: f.sourceTier,
        sourceRef: f.sourceRef,
      })),
    });
  }
  return { reportType, generatedAt, entries };
}

/** Normalized prompt block from a frozen manifest (preferred over raw claims). */
export function buildCoverageManifestPromptBlock(manifest: CoverageManifestAtGen): string {
  if (manifest.entries.length === 0) return '';
  const lines: string[] = [
    '═══ COVERAGE MATRIX — CONSOLIDATED REQUIREMENT MANIFEST ═══',
    '',
    `Report type: ${manifest.reportType}`,
    `Frozen at: ${manifest.generatedAt}`,
    '',
  ];
  for (const entry of manifest.entries) {
    lines.push(`[${entry.requirementId}]`);
    for (const f of entry.fields) {
      lines.push(`  ${f.key}: ${f.displayValue} (tier=${f.sourceTier}, ref=${f.sourceRef})`);
    }
    lines.push('');
  }
  lines.push('Use ONLY these normalized field values for requirement-dependent narrative sections.');
  lines.push('');
  return lines.join('\n');
}

/** Post-gen guard — returns field keys cited in narrative but absent from manifest. */
export function findManifestDrift(
  manifest: CoverageManifestAtGen,
  narrativeText: string,
): string[] {
  const drifts: string[] = [];
  const lower = narrativeText.toLowerCase();
  for (const entry of manifest.entries) {
    for (const f of entry.fields) {
      const display = f.displayValue.trim();
      if (display.length < 3 || display === '—') continue;
      if (lower.includes(display.toLowerCase())) continue;
      drifts.push(`${entry.requirementId}.${f.key}`);
    }
  }
  return drifts;
}
